// SEO utility functions for consistent meta data generation

export interface ProductSEOData {
  id: string;
  name: string;
  description?: string;
  category?: string;
  audience?: string;
  price?: number;
  thumbnail_url?: string;
  begard_approved?: boolean;
  for_sale?: boolean;
}

export interface PageSEOData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
}

/**
 * Generate SEO-friendly title for products
 */
export const generateProductTitle = (product: ProductSEOData): string => {
  const baseTitle = product.name;
  const category = product.category ? ` - ${formatCategory(product.category)}` : '';
  const audience = product.audience ? ` for ${formatAudience(product.audience)}` : '';
  
  return `${baseTitle}${category}${audience}`;
};

/**
 * Generate SEO description for products
 */
export const generateProductDescription = (product: ProductSEOData): string => {
  if (product.description) {
    // Clean markdown and truncate to ideal length
    const cleanDescription = cleanMarkdown(product.description);
    return truncateDescription(cleanDescription);
  }

  // Generate description from product data
  const parts = [];
  
  if (product.begard_approved) {
    parts.push('Begard-approved');
  }
  
  parts.push(product.name);
  
  if (product.category) {
    parts.push(`in the ${formatCategory(product.category)} category`);
  }
  
  if (product.audience) {
    parts.push(`designed for ${formatAudience(product.audience)}`);
  }
  
  parts.push('Professional bee sting prevention clothing and safety equipment.');
  
  if (product.price) {
    parts.push(`Starting at $${(product.price / 100).toFixed(2)}.`);
  }
  
  return parts.join(' ');
};

/**
 * Generate keywords for products
 */
export const generateProductKeywords = (product: ProductSEOData): string => {
  const keywords = [
    'bee sting prevention',
    'protective clothing',
    'safety equipment',
    'beekeeping gear'
  ];
  
  if (product.category) {
    keywords.push(product.category.toLowerCase());
  }
  
  if (product.audience) {
    keywords.push(product.audience.toLowerCase());
  }
  
  if (product.begard_approved) {
    keywords.push('begard approved', 'professional grade');
  }
  
  // Add product name words
  const nameWords = product.name.toLowerCase().split(' ').filter(word => word.length > 2);
  keywords.push(...nameWords);
  
  return Array.from(new Set(keywords)).join(', ');
};

/**
 * Generate canonical URL for products
 */
export const generateProductCanonical = (productId: string): string => {
  const baseUrl = process.env.REACT_APP_SITE_URL || 'https://begardgear.com';
  return `${baseUrl}/products/${productId}`;
};

/**
 * Generate page SEO data for different page types
 */
export const generatePageSEO = (pageType: string, data?: any): PageSEOData => {
  const baseUrl = process.env.REACT_APP_SITE_URL || 'https://begardgear.com';
  
  switch (pageType) {
    case 'home':
      return {
        title: 'Professional Bee Sting Prevention Clothing & Equipment',
        description: 'Discover expertly-curated bee and wasp sting prevention clothing and equipment. Shop professional-grade protective gear designed for outdoor safety, hiking, camping, and nature activities. Begard-approved quality you can trust.',
        keywords: 'bee sting prevention, wasp sting prevention, protective clothing, outdoor safety gear, beekeeping equipment, hiking protection, camping safety, nature enthusiast gear, insect deterrent clothing, professional protection',
        url: baseUrl,
        type: 'website'
      };
      
    case 'products':
      return {
        title: 'Bee & Wasp Sting Prevention Products | Protective Clothing & Gear',
        description: 'Shop our curated collection of bee and wasp sting prevention clothing, safety equipment, and protective gear. Light-colored, thick-fabric options perfect for hiking, camping, gardening, and outdoor adventures. Expert-selected products to keep you safe.',
        keywords: 'bee sting prevention products, wasp deterrent clothing, protective outdoor gear, hiking safety clothing, camping protective equipment, gardening safety gear, insect sting prevention, beekeeping protective wear, outdoor safety catalog',
        url: `${baseUrl}/products`,
        type: 'website'
      };
      
    case 'about':
      return {
        title: 'About Begard Gear | Our Mission to Protect Outdoor Enthusiasts',
        description: 'Discover how two siblings from Minnesota\'s Northwoods created Begard Gear to help families enjoy the outdoors safely. Our personal story of living with insect sting allergies inspired us to curate the best protective clothing and equipment for nature lovers.',
        keywords: 'about begard gear, insect sting allergy, outdoor safety mission, family outdoor safety, bee sting prevention story, protective gear company, Minnesota outdoor company, nature safety commitment',
        url: `${baseUrl}/about`,
        type: 'website'
      };
      
    case 'contact':
      return {
        title: 'Contact Begard Gear | Expert Advice on Sting Prevention',
        description: 'Have questions about bee and wasp sting prevention? Contact our team for expert advice on protective clothing, product recommendations, and outdoor safety tips. We understand living with insect allergies and are here to help.',
        keywords: 'contact begard gear, bee sting prevention advice, protective clothing questions, customer support, outdoor safety consultation, insect allergy help, product recommendations',
        url: `${baseUrl}/contact`,
        type: 'website'
      };
      
    case 'cart':
      return {
        title: 'Shopping Cart | Your Protective Gear Selection',
        description: 'Review your bee and wasp sting prevention products and complete your order. Fast, secure checkout with reliable shipping. Start enjoying safer outdoor adventures today.',
        keywords: 'shopping cart, protective gear checkout, bee sting prevention order, secure online shopping, outdoor safety equipment purchase',
        url: `${baseUrl}/cart`,
        type: 'website'
      };
      
    default:
      return {
        title: 'Begard Gear',
        description: 'Professional bee sting prevention clothing and equipment.',
        url: baseUrl,
        type: 'website'
      };
  }
};

/**
 * Clean markdown content for meta descriptions
 */
export const cleanMarkdown = (content: string): string => {
  return content
    .replace(/#{1,6}\s+/g, '') // Remove headers
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1') // Remove bold/italic
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
    .replace(/`([^`]+)`/g, '$1') // Remove code formatting
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();
};

/**
 * Truncate description to optimal length for meta tags
 */
export const truncateDescription = (description: string, maxLength: number = 155): string => {
  if (description.length <= maxLength) return description;
  
  // Find the last complete sentence or word within the limit
  const truncated = description.substring(0, maxLength);
  const lastSentence = truncated.lastIndexOf('.');
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSentence > maxLength * 0.8) {
    return truncated.substring(0, lastSentence + 1);
  } else if (lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace) + '...';
  } else {
    return truncated + '...';
  }
};

/**
 * Format category for display
 */
export const formatCategory = (category: string): string => {
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format audience for display
 */
export const formatAudience = (audience: string): string => {
  return audience
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Generate breadcrumb structured data
 */
export const generateBreadcrumbData = (breadcrumbs: Array<{name: string, url: string}>) => {
  const baseUrl = process.env.REACT_APP_SITE_URL || 'https://begardgear.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${baseUrl}${crumb.url}`
    }))
  };
};
